<script setup>
import { ref, inject } from 'vue';
import { useAssessmentStore } from '@/stores/assessment';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const assessmentStore = useAssessmentStore();
const { search, destroyAssessment } = assessmentStore;
const { all } = storeToRefs(assessmentStore);

const params = ref({
    term: '',
    is_active: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const activeOptions = [
    { label: trans('label.active'), value: true },
    { label: trans('label.inactive'), value: false },
];

const searchAssessment = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchAssessment();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchAssessment();
};

const openModal = (modal, mode = 'Create', assessment = null) => {
    modals.show(modal, {
        props: assessment ? {
            assessment,
            mode,
        } : {
            mode,
        }
    }).then(() => {
        searchAssessment();
    });
};

const remove = async (assessment) => {
    const confirm = await modals.show('ConfirmationModal', {
        title: trans('label.confirm_delete'),
        prompt: trans('label.are_you_sure_delete'),
    });

    if (confirm) {
        await destroyAssessment({ hash: assessment.hash });
        searchAssessment();
    }
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.assessment_customization') }}</template>
    <template #actions>
        <StyledButton @click="openModal('AssessmentsModal')">
            <FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.new_assessment') }}
        </StyledButton>
    </template>

    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchAssessment"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.is_active"
            clearable
            :placeholder="$t('label.status')"
            :options="activeOptions"
            name="is_active"
            @input="searchAssessment"
            @click:clear="searchAssessment" />
    </div>

    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'name', value: item => item.name, sortable: true },
            { title: $t('label.version'), key: 'version', value: item => item.version, sortable: true },
            { title: $t('label.assessment_type'), key: 'type', value: item => item.type?.name ?? '-', sortable: false },
            { title: $t('label.domain'), key: 'domain', value: item => item.domain?.name ?? '-', sortable: false },
            { title: $t('label.questions'), key: 'questions_count', value: item => item.questions_count, sortable: false },
            { title: $t('label.assigned_profiles'), key: 'member_assessments_count', value: item => item.member_assessments_count, sortable: false },
            { title: $t('label.status'), key: 'is_active', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            {
                title: $t('label.edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('AssessmentsModal', 'Edit', item),
            },
            {
                title: $t('label.delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
